import { useState, forwardRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import krewLogo from "@/assets/krew-logo.png";

const WaitlistForm = forwardRef<HTMLDivElement>((_, ref) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-signup", {
        body: { email: email.trim() },
      });
      if (error) throw error;
      if (data?.duplicate) {
        toast({ title: "You're already on the list! 🎉" });
      } else {
        toast({ title: "You're in! 🎉", description: "We'll notify you when we launch." });
      }
      setSuccess(true);
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Something went wrong", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div ref={ref} className="mx-auto flex max-w-md items-center justify-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-6 py-4">
        <Check className="h-5 w-5 text-primary" />
        <span className="text-sm font-semibold text-primary">You're on the list!</span>
      </div>
    );
  }

  return (
    <div ref={ref}>
      <form onSubmit={handleSubmit} className="mx-auto flex max-w-md flex-col gap-3 sm:flex-row">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className="flex-1 rounded-xl border border-border bg-muted px-5 py-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button type="submit" disabled={loading} className="group inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02] disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Get Access <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
        </button>
      </form>
      <p className="mt-4 text-xs text-muted-foreground">
        Free forever. No credit card needed.
      </p>
    </div>
  );
});

WaitlistForm.displayName = "WaitlistForm";

const CTASection = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section ref={ref} id="cta" className="section-padding bg-card">
      <div className="mx-auto max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.img
            src={krewLogo}
            alt="Krew"
            className="mx-auto mb-6 h-16 w-16"
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          />
          <h2 className="mb-6 text-4xl font-bold md:text-6xl">
            Ready to <span className="text-gradient-logo">Krew</span> tonight?
          </h2>
          <p className="mx-auto mb-10 max-w-xl text-lg text-muted-foreground font-body leading-relaxed">
            Start your first Krew tonight. Free forever.
            Your next legendary night is 5 minutes away.
          </p>

          <WaitlistForm />
        </motion.div>
      </div>
    </section>
  );
});

CTASection.displayName = "CTASection";

export default CTASection;
